/**
 * NotaSoape — la nota clínica presentada como DOCUMENTO, no como formulario.
 *
 * Cada sección (S·O·A·P·E) se lee como texto corrido; al hacer clic se vuelve
 * editable en el sitio (ver EditableText). Conserva íntegro el comportamiento
 * del rediseño NU:
 *   - La sección editada por el médico queda marcada ("editado por ti") y el
 *     servidor la protege de los ciclos de IA.
 *   - Cuando la IA tiene una versión nueva de una sección protegida, aparece
 *     como PROPUESTA discreta con aceptar/descartar.
 *   - Un destello "Guardado" confirma la escritura sin botones de guardar.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, Check, X } from 'lucide-react';
import type { SOAPData } from '../../services/medicalAI';
import { EditableText } from './EditableText';
import styles from './NotaSoape.module.css';

type SoapKey = keyof SOAPData;

interface SeccionDef {
  key: SoapKey;
  letra: string;
  titulo: string;
  hint: string;
  placeholder: string;
}

const SECCIONES: SeccionDef[] = [
  {
    key: 's', letra: 'S', titulo: 'Subjetivo', hint: 'relato del paciente',
    placeholder: 'La IA lo completará con la conversación — o haga clic y escríbalo usted.',
  },
  {
    key: 'o', letra: 'O', titulo: 'Objetivo', hint: 'hallazgos y signos',
    placeholder: 'Se llenará con lo observable en la llamada.',
  },
  {
    key: 'a', letra: 'A', titulo: 'Análisis', hint: 'impresión diagnóstica',
    placeholder: 'Aparecerá cuando el médico establezca el diagnóstico.',
  },
  {
    key: 'p', letra: 'P', titulo: 'Plan', hint: 'tratamiento y seguimiento',
    placeholder: 'Se irá armando con lo que usted indique en la consulta.',
  },
  {
    key: 'e', letra: 'E', titulo: 'Evolución', hint: 'respuesta al tratamiento',
    placeholder: 'En una primera consulta suele quedar vacía.',
  },
];

interface NotaSoapeProps {
  soap: SOAPData;
  onChange: (key: SoapKey, value: string) => void;
  pendingAIUpdates: Partial<SOAPData>;
  onAccept: (key: SoapKey) => void;
  onDismiss: (key: SoapKey) => void;
  recentlyUpdated: Set<SoapKey>;
  doctorEdited: Set<SoapKey>;
  /** Nota firmada: se muestra igual pero no se puede editar. */
  readOnly?: boolean;
}

export function NotaSoape({
  soap,
  onChange,
  pendingAIUpdates,
  onAccept,
  onDismiss,
  recentlyUpdated,
  doctorEdited,
  readOnly = false,
}: NotaSoapeProps) {
  // Destello "Guardado" por sección tras confirmar una edición.
  const [guardadas, setGuardadas] = useState<Set<SoapKey>>(new Set());
  const timersRef = useRef<Partial<Record<SoapKey, ReturnType<typeof setTimeout>>>>({});

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of Object.values(timers)) if (t) clearTimeout(t);
    };
  }, []);

  const confirmar = useCallback(
    (key: SoapKey, valor: string) => {
      onChange(key, valor);
      setGuardadas((prev) => new Set(prev).add(key));
      if (timersRef.current[key]) clearTimeout(timersRef.current[key]);
      timersRef.current[key] = setTimeout(() => {
        setGuardadas((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }, 1800);
    },
    [onChange],
  );

  return (
    <article className={styles.doc}>
      {SECCIONES.map((sec) => {
        const propuesta = pendingAIUpdates[sec.key];
        const resaltada = recentlyUpdated.has(sec.key);
        return (
          <section
            key={sec.key}
            className={`${styles.sec} ${resaltada ? styles.secResaltada : ''}`}
          >
            <span className={styles.insignia} aria-hidden="true">{sec.letra}</span>
            <div className={styles.cuerpo}>
              <header className={styles.encabezado}>
                <h4 className={styles.titulo}>{sec.titulo}</h4>
                <span className={styles.hint}>{sec.hint}</span>
                {doctorEdited.has(sec.key) && (
                  <span className={`${styles.chip} ${styles.chipEditado}`}>editado por usted</span>
                )}
                {resaltada && !doctorEdited.has(sec.key) && (
                  <span className={`${styles.chip} ${styles.chipIa}`}>
                    <Sparkles size={9} aria-hidden="true" /> actualizado
                  </span>
                )}
                {guardadas.has(sec.key) && (
                  <span className={`${styles.chip} ${styles.chipGuardado}`}>
                    <Check size={9} aria-hidden="true" /> Guardado
                  </span>
                )}
              </header>

              <EditableText
                value={soap[sec.key] ?? ''}
                onCommit={(v) => confirmar(sec.key, v)}
                placeholder={sec.placeholder}
                ariaLabel={`${sec.titulo} — ${sec.hint}`}
                disabled={readOnly}
              />

              {propuesta && (
                <aside className={styles.propuesta}>
                  <span className={styles.propuestaTag}>
                    <Sparkles size={10} aria-hidden="true" />
                    La IA tiene una versión nueva — usted editó esta sección, así que no se aplicó sola
                  </span>
                  <p className={styles.propuestaTexto}>{propuesta}</p>
                  <div className={styles.propuestaAcciones}>
                    <button
                      type="button"
                      className={styles.btnAceptar}
                      onClick={() => onAccept(sec.key)}
                    >
                      <Check size={12} aria-hidden="true" /> Reemplazar
                    </button>
                    <button
                      type="button"
                      className={styles.btnDescartar}
                      onClick={() => onDismiss(sec.key)}
                    >
                      <X size={12} aria-hidden="true" /> Descartar
                    </button>
                  </div>
                </aside>
              )}
            </div>
          </section>
        );
      })}
    </article>
  );
}
